/**
 * #2712 CLIENTERROR: the one contract for a browser error report.
 *
 * On 16 Sep two people hit an error on /commercial/deliveries and nothing saw
 * it: every API call behind the page answered 200, and the error happened in
 * the browser. The client reporter (src/client/lib/client-error-report.ts)
 * posts uncaught errors, unhandled rejections and error-boundary catches to
 * POST /api/client-error, which writes one structured Worker log line
 * (`event: "client_error"`) for the observability query API to read.
 *
 * Both sides run this normaliser. The client runs it so the body it sends is
 * already the size the server keeps; the server runs it again because the
 * client is never trusted. It keeps only the fields named here (anything else
 * in the body is dropped), caps each one, and scrubs what could carry a
 * person or a record:
 *   - `page` is the template of the app route the path renders under
 *     (`/commercial/cases/:id`, never `/commercial/cases/42`), from the routes
 *     App.tsx declares (CLIENT_ROUTES); a path no route matches is logged as
 *     "[unknown page]", so no part of a path the caller wrote reaches the line;
 *   - `stack` keeps only frames in the app's own bundle on the page's own
 *     origin, each logged as `    at /assets/<name>-<hash>.js:<line>:<col>`
 *     and nothing else: no function name, no other URL, never the message an
 *     engine repeats in a stack's head (the route also drops a file the
 *     deployment does not serve as a script);
 *   - query strings and hashes are cut from every URL inside `message`;
 *   - an email address anywhere becomes "[email]";
 *   - the token in a portal or share link, and any 32-hex path segment, in
 *     a URL in the message, becomes ":token";
 *   - an ApiError's "API <status>: <response body>" keeps the status only;
 *   - control and bidirectional-override characters are removed, so a
 *     message cannot forge a second line or reorder what a reader sees;
 *   - `boundary` is one of CLIENT_ERROR_BOUNDARIES, the app's own error
 *     boundaries; any other is logged as "[boundary withheld]";
 *   - a message the reporter would not write (isAllowedMessage, in
 *     client-error-messages.ts) is logged as MESSAGE_NOT_ALLOWED, and a
 *     `build` that is not a build id's shape as BUILD_NOT_ALLOWED.
 *
 * The server adds the caller's role ids and the request's ray id itself; the
 * line never carries a user id, email or name. Browser-safe on purpose: no
 * lookbehind (Safari before 16.4 refuses to parse it), no Node APIs.
 */

import { MESSAGE_NOT_ALLOWED, isAllowedMessage } from "./client-error-messages";
import CLIENT_ERROR_SETS from "./client-error-sets.v1.json";

export const CLIENT_ERROR_KINDS = ["error", "unhandledrejection", "boundary"] as const;
export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

export const CLIENT_ERROR_LIMITS = {
  /** Characters (code points) kept of the message. */
  messageChars: 300,
  /** Frames kept from the top of the stack. */
  stackLines: 5,
  /** Characters (code points) kept of those lines, newlines included. */
  stackChars: 1000,
  pageChars: 200,
  /** The route refuses a larger body (413) before it parses anything. */
  bodyBytes: 8 * 1024,
} as const;

/** Per session; the route answers 429 past it and writes no line. */
export const CLIENT_ERROR_RATE_LIMIT = { reports: 10, windowMs: 10 * 60 * 1000 } as const;

/**
 * The error boundaries that report (the `boundary` a report may name). A
 * boundary passes its own literal, which the type holds to this list.
 * Review (round 2, High): any identifier passed, so a caller could name one
 * `JoBloggs0400000000`.
 */
export const CLIENT_ERROR_BOUNDARIES = ["ChunkLoadErrorBoundary"] as const;
export type ClientErrorBoundary = (typeof CLIENT_ERROR_BOUNDARIES)[number];
/** Logged in place of a boundary that is not on CLIENT_ERROR_BOUNDARIES. */
export const BOUNDARY_WITHHELD = "[boundary withheld]";

/** The fields a report may carry. The normaliser builds a fresh object from these and nothing else. */
export interface ClientErrorReport {
  kind: ClientErrorKind;
  page: string;
  message: string;
  stack?: string;
  boundary?: string;
  build: string;
}

// C0 controls except tab and newline, DEL, C1 controls, and the bidi marks
// and overrides (LRM, RLM, LRE..RLO, LRI..PDI). Tab and newline are handled
// per field below.
const STRIP_CHARS = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F‎‏‪-‮⁦-⁩]/g;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}/gi;
// An absolute URL that goes on into a query string or a hash, anywhere
// (Safari and Firefox frames read `fn@https://...`).
const ABSOLUTE_URL_WITH_QUERY = /\b([a-z][a-z0-9+.-]*:\/\/[^\s?#"'`()<>]*)([?#][^\s"'`()<>]*)/gi;
// A bare path that starts a token and goes on into a query string or a hash.
// The first group keeps the character before the path.
const PATH_WITH_QUERY = /(^|[\s"'`(<[@=,])(\/[^\s?#"'`()<>]*)([?#][^\s"'`()<>]*)/gm;
const LINE_COL = /:\d+(?::\d+)?$/;
// An ApiError's message is "API <status>: <response body>" (src/client/lib/
// api-error.ts). The body is server text that can carry a person's or a
// material's name, so only the status survives, here as well as in the client.
const API_ERROR_BODY = /\bAPI (\d{3}): .*$/;
const TOKEN_IN_TEXT = /(\/(?:portal|shared)\/)[^\s/?#:"'`()<>]+/gi;
// lib/random-token.ts mints 32 lower-case hex characters; a URL segment that
// long and that shape is a token wherever it sits.
const TOKEN_HEX_IN_TEXT = /\/[0-9a-f]{32,}(?=$|[/?#:\s"'`()<>.,;\]}])/gi;
// A stack frame, whole: a name and a LOCATION, nothing else. Every other line
// of a stack is the message repeating (an engine writes it into the head,
// every line of a multi-line one), so it goes. Review round 3: a prefix check
// let "    at <a person's name>" from a response body through.
//   V8 (Chrome, Edge): "    at fn (url:1:2)", "    at url:1:2",
//     "    at async fn (url:1:2)", "    at new Foo (<anonymous>)",
//     "    at Object.fn [as m] (url:1:2)", "    at async Promise.all (index 0)",
//     "    at get value (url:1:2)", "    at <anonymous>".
//   JavaScriptCore (Safari), SpiderMonkey (Firefox): "fn@url:1:2", "@url:1:2",
//     "global code@url:1:2", "fn@[native code]", "fn/<@url:1:2", "async*fn@url:1:2".
// Of those, capStack keeps only a frame whose location is the app's bundle
// (ASSET_FRAME_LOCATION below).
const FRAME_URL = String.raw`(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s()]+:\d+(?::\d+)?`;
const V8_LOCATION = String.raw`(?:${FRAME_URL}|<anonymous>|native|index \d+)`;
const V8_FRAME = new RegExp(
  String.raw`^\s+at (?:(?:async |new |get |set )?[\w$.<>]+(?: \[as [\w$.<>]+\])? \(${V8_LOCATION}\)|(?:async )?(?:${FRAME_URL}|<anonymous>))$`,
  "i",
);
const AT_FRAME = new RegExp(
  String.raw`^(?:(?:global|module|eval) code|promise callback\*[\w$.<>/*]*|[\w$.<>/*]*)@(?:${FRAME_URL}|\[native code\])$`,
  "i",
);
/** True for a whole stack frame line. */
export function isStackFrame(line: string): boolean {
  return V8_FRAME.test(line) || AT_FRAME.test(line);
}
/**
 * The client's BUILD_ID, as vite.config.ts (resolveBuildId) and
 * src/shared/build-id.ts make it: a 12-character commit sha, "build-<ms>"
 * when the build had no git, or "dev" where the define did not run. Review
 * (4132362fc): any 64 characters of [A-Za-z0-9._-] passed, so a caller could
 * write a name or a number into the line's build.
 */
const BUILD_ID_SHAPE = /^(?:[0-9a-f]{12}|build-\d{13}|dev)$/;
/** Logged in place of a build that is not a build id (a client that is not this app's reporter). */
export const BUILD_NOT_ALLOWED = "[build not allowed]";
/**
 * Logged by the route in place of a build id that is not the deployment's
 * own: a tab still running an older deployment, or a caller's 12 hex
 * characters. Review round 4: `aa0400000000` is a build id's shape, and
 * carried a phone number's digits into the line.
 */
export const OTHER_BUILD = "[other build]";

/** Keep at most `max` code points, never splitting a surrogate pair. */
function capChars(s: string, max: number): string {
  // Bound the work first: a code point is at most two UTF-16 units.
  const bounded = s.length > max * 2 ? s.slice(0, max * 2) : s;
  const points = Array.from(bounded);
  return points.length > max ? points.slice(0, max).join("") : bounded;
}

/**
 * The head of `s` the scrubbers may read: twice the field's cap. Scrubbing
 * only shortens text, so this keeps everything the cap could keep, and it
 * bounds the regexes' work (an 8 KB body of `a.a.a...` cost ~50 ms a field
 * before; measured 28 Oct 2026).
 */
function bound(s: string, cap: number): string {
  if (s.length <= cap * 2) return s;
  const head = s.slice(0, cap * 2);
  // Never leave half a surrogate pair at the cut.
  return /[\uD800-\uDBFF]$/.test(head) ? head.slice(0, -1) : head;
}

/**
 * Cut the query string and hash from every URL in `s`. A stack frame's
 * trailing `:line:col` survives, so `https://x/p?q=1:12:5` becomes
 * `https://x/p:12:5`.
 */
export function stripUrlQueries(s: string): string {
  const keepLineCol = (query: string) => LINE_COL.exec(query)?.[0] ?? "";
  return s
    .replace(ABSOLUTE_URL_WITH_QUERY, (_m, url: string, query: string) => `${url}${keepLineCol(query)}`)
    .replace(PATH_WITH_QUERY, (_m, before: string, path: string, query: string) => `${before}${path}${keepLineCol(query)}`);
}

function scrub(s: string): string {
  return stripUrlQueries(s.replace(STRIP_CHARS, ""))
    .replace(EMAIL, "[email]")
    .replace(TOKEN_IN_TEXT, "$1:token")
    .replace(TOKEN_HEX_IN_TEXT, "/:token");
}

/**
 * The route templates App.tsx declares, generated into
 * client-error-sets.v1.json by scripts/gen-client-error-sets.mjs (a test fails
 * when the file is stale). The catch-all `*` is not among them.
 */
export const CLIENT_ROUTES: readonly string[] = CLIENT_ERROR_SETS.routes;

/** Logged in place of a path no route of the app's matches (its not-found page, or a path a caller made up). */
export const UNKNOWN_PAGE = "[unknown page]";

const ROUTE_SHAPES = CLIENT_ROUTES.map((template) => {
  const segments = template.split("/").filter(Boolean).map((seg) => seg.toLowerCase());
  return { template, segments, statics: segments.filter((seg) => !seg.startsWith(":")).length };
});

/**
 * The template of the route `pathname` renders under, matched as React Router
 * matches App.tsx's routes: segment by segment, ignoring case and a trailing
 * slash, a `:param` taking any one segment, the route with more fixed
 * segments winning. Null when no route matches.
 */
export function routeTemplate(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean).map((seg) => seg.toLowerCase());
  let best: { template: string; statics: number } | null = null;
  for (const route of ROUTE_SHAPES) {
    if (route.segments.length !== segments.length) continue;
    if (!route.segments.every((seg, i) => seg.startsWith(":") || seg === segments[i])) continue;
    if (!best || route.statics > best.statics) best = route;
  }
  return best ? best.template : null;
}

/**
 * The page a report logs: the template of the route its path renders under,
 * or UNKNOWN_PAGE. Null when `raw` is not a path or URL at all (the route
 * answers 400). A query string or hash never takes part. UNKNOWN_PAGE itself
 * is kept, exactly: it is what the client sends for a path no route matches,
 * and the route normalises that body again (review round 4: it was a 400,
 * and the report was lost).
 */
export function reportPage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  if (raw.trim() === UNKNOWN_PAGE) return UNKNOWN_PAGE;
  let s = bound(raw.trim(), CLIENT_ERROR_LIMITS.pageChars);
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) {
    try {
      s = new URL(s).pathname;
    } catch {
      return null;
    }
  }
  s = s.replace(STRIP_CHARS, "").split(/[?#]/, 1)[0].replace(/\s+/g, "");
  if (!s.startsWith("/")) return null;
  return routeTemplate(s) ?? UNKNOWN_PAGE;
}

/** One line, scrubbed, capped. Empty after cleaning means there was no message. Not the allowlist: normaliseClientErrorReport applies that. */
export function capMessage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = scrub(bound(raw, CLIENT_ERROR_LIMITS.messageChars).replace(/[\r\n\t]+/g, " "))
    .replace(API_ERROR_BODY, "API $1")
    .replace(/ {2,}/g, " ")
    .trim();
  return s ? capChars(s, CLIENT_ERROR_LIMITS.messageChars) : null;
}

/**
 * Where a frame the log keeps points: the app's own bundle, as Vite names its
 * files (`/assets/<name>-<hash>.js`, an 8-character hash), with a line and a
 * column, at the end of the frame line. The URL is either on the page's own
 * origin (capStack checks the first group) or the bare path a normalised
 * frame already carries. Review (e328b92e2): a frame's function name and URL
 * were the caller's to write, so a person's name or a phone number could ride
 * in either. The line and column are bounded to a bundle's size (911 lines
 * and 242,013 columns at most in the 29 Oct 2026 build).
 */
const ASSET_FRAME_LOCATION = /(?:^|[\s(@])([a-z][a-z0-9+.-]*:\/\/[^\s/()@]+)?\/assets\/([\w-]{1,80}-[\w-]{8}\.js):(0|[1-9]\d{0,5}):(0|[1-9]\d{0,6})\)?$/i;
/** A frame as capStack writes it. */
const NORMALISED_FRAME = /^ {4}at \/assets\/([\w-]{1,80}-[\w-]{8}\.js):\d{1,6}:\d{1,7}$/;

/** The bundle file a normalised frame names, for the route's check that the deployment serves it. */
export function frameAssetFile(frame: string): string | null {
  return NORMALISED_FRAME.exec(frame)?.[1] ?? null;
}

/**
 * The first five stack frames that point into the app's bundle on `origin`
 * (the page's own; ASSET_FRAME_LOCATION), each written as
 * `    at /assets/<file>:<line>:<col>`; every other line, and every other part
 * of a kept line, dropped. Absent when no frame is left.
 */
export function capStack(raw: unknown, origin: string): string | undefined {
  if (typeof raw !== "string") return undefined;
  const site = origin.toLowerCase();
  const frames: string[] = [];
  for (const line of bound(raw, CLIENT_ERROR_LIMITS.stackChars).replace(/\r\n?/g, "\n").split("\n")) {
    if (frames.length === CLIENT_ERROR_LIMITS.stackLines) break;
    if (!isStackFrame(line)) continue;
    const at = ASSET_FRAME_LOCATION.exec(line);
    if (!at || (at[1] !== undefined && at[1].toLowerCase() !== site)) continue;
    frames.push(`    at /assets/${at[2]}:${at[3]}:${at[4]}`);
  }
  return frames.length ? frames.join("\n") : undefined;
}

/**
 * The report the route logs, or null when a required field is missing or
 * malformed (the route answers 400). `kind`, `page`, `message` and `build` are
 * required; `stack` is dropped when no frame passes, and a `boundary` that
 * is not one of CLIENT_ERROR_BOUNDARIES is kept as BOUNDARY_WITHHELD. A
 * message off the list is kept as MESSAGE_NOT_ALLOWED, and a build that is
 * not a build id as BUILD_NOT_ALLOWED, so the report still logs its page and
 * stack. Every other key on `input` is ignored. `origin` is the page's: the
 * browser's own, or the origin of the request the route serves.
 */
export function normaliseClientErrorReport(input: unknown, origin: string): ClientErrorReport | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const raw = input as Record<string, unknown>;
  const kind = (CLIENT_ERROR_KINDS as readonly unknown[]).includes(raw.kind) ? (raw.kind as ClientErrorKind) : null;
  const page = reportPage(raw.page);
  const capped = capMessage(raw.message);
  const message = capped === null ? null : isAllowedMessage(capped) ? capped : MESSAGE_NOT_ALLOWED;
  const sentBuild = typeof raw.build === "string" ? raw.build.trim() : "";
  const build = !sentBuild ? null : BUILD_ID_SHAPE.test(sentBuild) ? sentBuild : BUILD_NOT_ALLOWED;
  if (!kind || !page || !message || !build) return null;
  const report: ClientErrorReport = { kind, page, message, build };
  const stack = capStack(raw.stack, origin);
  if (stack) report.stack = stack;
  const boundary = typeof raw.boundary === "string" ? raw.boundary.trim() : "";
  if (boundary) report.boundary = (CLIENT_ERROR_BOUNDARIES as readonly string[]).includes(boundary) ? boundary : BOUNDARY_WITHHELD;
  return report;
}
